import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CHeader } from '../../components/common';
import {
  FiCalendar,
  FiDollarSign,
  FiShield,
  FiCreditCard,
  FiChevronRight,
  FiUser,
} from 'react-icons/fi';
import routes from '../../utils/routes';

interface SettingsItem {
  title: string;
  description: string;
  icon: any;
  route: string;
  badge?: string;
}

const ExpertSettings: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);
  const user = useAppSelector((state) => state.user.user);

  const settingsItems: SettingsItem[] = [
    {
      title: 'Availability',
      description: 'Set your weekly schedule and block specific dates',
      icon: FiCalendar,
      route: routes.expertAvailability || '/expert/settings/availability',
    },
    {
      title: 'Session Rates',
      description: 'Configure your video call and chat session pricing',
      icon: FiDollarSign,
      route: routes.expertRates || '/expert/settings/rates',
    },
    {
      title: 'SEBI Credentials',
      description: 'Update your SEBI registration and qualifications',
      icon: FiShield,
      route: routes.expertSebiCredentials || '/expert/settings/sebi',
    },
    {
      title: 'Wallet & Payouts',
      description: 'Manage your earnings and payout settings',
      icon: FiCreditCard,
      route: routes.expertWallet || '/expert/settings/wallet',
      badge: 'New',
    },
  ];

  return (
    <div
      className="min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      {/* Header */}
      <div className="p-4 lg:p-8">
        <div className="lg:max-w-2xl lg:mx-auto">
          <CHeader title="Expert Settings" />

          {/* Profile Summary Card */}
          <div
            className="rounded-2xl p-5 mb-6"
            style={{
              background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
            }}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
              >
                {user?.image ? (
                  <img
                    src={user.image}
                    alt={user.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  React.createElement(FiUser as any, { size: 28, color: '#fff' })
                )}
              </div>
              <div className="flex-1">
                <CText type="B18" color="#fff">
                  {user?.name || 'Expert'}
                </CText>
                <CText type="r14" color="rgba(255,255,255,0.8)">
                  {user?.headline || 'SEBI Registered Advisor'}
                </CText>
                <div className="flex items-center gap-2 mt-1">
                  {user?.is_verified && (
                    <span
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' }}
                    >
                      Verified
                    </span>
                  )}
                  <span
                    className="px-2 py-0.5 rounded text-xs font-medium"
                    style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' }}
                  >
                    {user?.years_of_xp || 0} years exp
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Settings List */}
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              backgroundColor: colors.cardBackground || colors.inputBackground,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            {settingsItems.map((item, index) => (
              <button
                key={item.title}
                onClick={() => navigate(item.route)}
                className="w-full flex items-center gap-4 p-4 text-left transition-all hover:opacity-80"
                style={{
                  borderBottom: index < settingsItems.length - 1 ? `1px solid ${colors.borderColor}` : 'none',
                }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: colors.primary + '15' }}
                >
                  {React.createElement(item.icon as any, { size: 22, color: colors.primary })}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <CText type="M14" color={colors.textColor}>
                      {item.title}
                    </CText>
                    {item.badge && (
                      <span
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{ backgroundColor: colors.primary, color: '#fff' }}
                      >
                        {item.badge}
                      </span>
                    )}
                  </div>
                  <CText type="r12" color={colors.grayScale500}>
                    {item.description}
                  </CText>
                </div>
                {React.createElement(FiChevronRight as any, { size: 20, color: colors.grayScale500 })}
              </button>
            ))}
          </div>

          {/* Quick Stats */}
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div
              className="rounded-xl p-4"
              style={{
                backgroundColor: colors.cardBackground || colors.inputBackground,
                border: `1px solid ${colors.borderColor}`,
              }}
            >
              <CText type="r12" color={colors.grayScale500}>
                This Month Earnings
              </CText>
              <CText type="B20" color={colors.textColor}>
                Rs.{user?.total_earnings || '0'}
              </CText>
            </div>
            <div
              className="rounded-xl p-4"
              style={{
                backgroundColor: colors.cardBackground || colors.inputBackground,
                border: `1px solid ${colors.borderColor}`,
              }}
            >
              <CText type="r12" color={colors.grayScale500}>
                Total Sessions
              </CText>
              <CText type="B20" color={colors.textColor}>
                {user?.total_sessions || 0}
              </CText>
            </div>
          </div>

          {/* Help Section */}
          <div
            className="mt-6 rounded-xl p-4"
            style={{
              backgroundColor: colors.primary + '10',
              border: `1px solid ${colors.primary}30`,
            }}
          >
            <CText type="M14" color={colors.primary}>
              Need help?
            </CText>
            <CText type="r12" color={colors.grayScale500} style={{ marginTop: 4 }}>
              Contact our expert support team for assistance with your profile setup.
            </CText>
            <button
              className="mt-3 px-4 py-2 rounded-lg"
              style={{ backgroundColor: colors.primary }}
              onClick={() => navigate(routes.helpCenter || '/help')}
            >
              <CText type="M12" color="#fff">
                Contact Support
              </CText>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpertSettings;
